/**
 * Loan Products Grid Section
 * Fetches data from CMS API
 */
import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Calculator } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import axios from 'axios';
import { sanitizeHtml } from '../../../utils/sanitizeHtml';
import { isSafeUrl } from '../../../utils/isSafeUrl';

const CMS_API_URL = process.env.REACT_APP_API_BASE_URL_CMS || '';
const LOAN_PRODUCTS_CATEGORY_ID = 55;

const getLoanId = (title) => {
  if (!title) return 'loan';
  const lower = title.toLowerCase();
  if (lower.includes('emergency')) return 'emergency';
  if (lower.includes('development')) return 'development';
  if (lower.includes('education')) return 'education';
  if (lower.includes('chapchap')) return 'chapchap';
  return title.toLowerCase().replace(/\s+/g, '-');
};

const getLoanAnchorId = (id) => {
  if (id === 'emergency') return 'emergency-loan';
  if (id === 'development') return 'development-loan';
  if (id === 'education') return 'education-loan';
  if (id === 'chapchap') return 'chapchap-loan';
  return id;
};

const stripHtml = (html) => {
  if (!html) return '';
  return html
    .replace(/&nbsp;/g, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const parseJsonArray = (str) => {
  if (!str) return [];
  try {
    const parsed = JSON.parse(str);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const LoanProductsGrid = ({ onCalculate }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [sectionData, setSectionData] = useState(null);
  const [loanProducts, setLoanProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleToggle = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const hasExpandedCard = expandedId !== null;

  // Fetch section title and loan products from CMS API
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch category name and description
        const categoryResponse = await axios.post(
          `${CMS_API_URL}categories/get`,
          { CategoryId: LOAN_PRODUCTS_CATEGORY_ID },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
          }
        );

        if (categoryResponse.data?.code === 1 && categoryResponse.data?.data) {
          const data = categoryResponse.data.data;
          setSectionData({
            Title: data.Name,
            Description: data.Description,
          });
        }

        // Fetch loan products
        const productsResponse = await axios.post(
          `${CMS_API_URL}products/published`,
          { CategoryId: LOAN_PRODUCTS_CATEGORY_ID },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
          }
        );

        if (productsResponse.data?.code === 1 && Array.isArray(productsResponse.data?.data)) {
          const validProducts = productsResponse.data.data
            .filter(item => item.IsDeleted === false && item.IsPublished === true)
            .map(item => {
              const id = getLoanId(item.Title);
              return {
                id,
                icon: LucideIcons[item.Icon],
                title: item.Title || 'Loan',
                description: stripHtml(item.Description),
                specs: [
                  { label: 'Interest Rate', value: item.InterestRate || '' },
                  { label: 'Max Amount', value: item.MaxAmount || '' },
                  { label: 'Max Tenure', value: item.MaxTenure || '' },
                ].filter(s => s.value),
                qualifiesFor: parseJsonArray(item.QualifiesFor),
                requirements: parseJsonArray(item.Requirements),
                qualifiesTitle: item.QualifiesTitle || 'QUALIFIES FOR:',
                requirementsTitle: item.RequirementsTitle || 'REQUIREMENTS:',
                link: item.Link || '',
              };
            });
          setLoanProducts(validProducts);
        }
      } catch (error) {
        console.error('Failed to fetch loan products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Return null if no data after loading
  if (!loading && loanProducts.length === 0 && !sectionData) {
    return null;
  }

  // Loading skeleton
  if (loading) {
    return (
      <section className="py-8 md:py-8 bg-[#faf6f0]">
        <div className="container mx-auto px-6 md:px-8 lg:px-12">
          <div className="h-8 w-48 bg-gray-200 rounded mb-2 animate-pulse" />
          <div className="h-4 w-96 bg-gray-200 rounded mb-8 animate-pulse" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-2xl p-6 h-64 animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-8 md:py-8 bg-[#faf6f0]">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        {/* loan grid sectionTitle */}
        {sectionData?.Title && (
          <h2 className="text-2xl md:text-3xl font-bold text-[#2f3951] mb-2">{sectionData.Title}</h2>
        )}
        {sectionData?.Description && (
          <p
            className="text-gray-500 text-sm mb-8 max-w-xl"
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(sectionData.Description.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim())
            }}
          />
        )}

        {/* Loan Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
          {loanProducts.map((product) => {
            const Icon = product.icon;
            const isExpanded = expandedId === product.id;

            return (
              <div
                key={product.id}
                id={getLoanAnchorId(product.id)}
                className={`bg-white rounded-2xl overflow-hidden flex flex-col transition-all duration-500 ease-out ${
                  isExpanded
                    ? 'border-2 border-[#ca9a37] shadow-2xl ring-1 ring-[#ca9a37]/20 z-10'
                    : hasExpandedCard
                      ? 'border border-gray-200 shadow-sm opacity-50'
                      : 'border border-gray-200 shadow-sm hover:shadow-md'
                }`}
              >
                {/* Card Header */}
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-3">
                    <div className="w-12 h-12 bg-[#f8f1e5] rounded-xl flex items-center justify-center flex-shrink-0">
                      {Icon && <Icon className="w-6 h-6 text-[#ca9a37]" />}
                    </div>
                    <h3 className="text-xl font-bold text-[#2f3951]">{product.title}</h3>
                  </div>
                  <p className="text-gray-500 text-sm leading-relaxed">{product.description}</p>
                </div>

                {/* Specs Table */}
                <div className="px-6 py-4 border-t border-gray-100 grid grid-cols-3 divide-x divide-gray-200">
                  {product.specs.map((spec, idx) => (
                    <div key={idx} className="text-center px-2">
                      <p className="text-gray-400 text-xs mb-1">{spec.label}</p>
                      <p className="text-[#ca9a37] text-sm font-semibold">{spec.value}</p>
                    </div>
                  ))}
                </div>

                {/* Expanded Content */}
                <div
                  className={`overflow-hidden transition-all duration-500 ease-out ${
                    isExpanded ? 'max-h-[700px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-6 py-5 border-t border-gray-100 bg-gray-50/50">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Qualifies For */}
                      <div>
                        <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">
                          {product.qualifiesTitle}
                        </h4>
                        <ul>
                          {product.qualifiesFor.map((item, idx) => (
                            <li
                              key={idx}
                              className={`flex items-start py-2 ${
                                idx !== product.qualifiesFor.length - 1 ? 'border-b border-gray-100' : ''
                              }`}
                            >
                              <span className="text-[#ca9a37] mr-2 flex-shrink-0 text-xs mt-1">&#9656;</span>
                              <span className="text-gray-600 text-sm">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Requirements */}
                      <div>
                        <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">
                          {product.requirementsTitle}
                        </h4>
                        <ul>
                          {product.requirements.map((item, idx) => (
                            <li
                              key={idx}
                              className={`flex items-start py-2 ${
                                idx !== product.requirements.length - 1 ? 'border-b border-gray-100' : ''
                              }`}
                            >
                              <span className="text-[#ca9a37] mr-2 flex-shrink-0 text-xs mt-1">&#9656;</span>
                              <span className="text-gray-600 text-sm">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom Action Bar */}
                <div className="bg-[#f8f1e5] px-6 py-4 flex items-center justify-between gap-2 mt-auto flex-wrap">
                  <button
                    onClick={() => onCalculate(product)}
                    className="bg-[#ca9a37] hover:bg-[#b88a2f] text-white text-sm font-medium px-5 py-2 rounded-lg transition-colors flex items-center"
                  >
                    <Calculator className="w-4 h-4 mr-1.5" />
                    Calculate
                  </button>
                  <a
                    href={(isSafeUrl(product.link) && product.link) || 'https://ess.utumishi.go.tz'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="border border-gray-300 hover:border-[#ca9a37] text-gray-600 hover:text-[#ca9a37] text-sm font-medium px-5 py-2 rounded-lg transition-colors bg-white inline-block text-center"
                  >
                    Apply Now
                  </a>
                  <button
                    onClick={() => handleToggle(product.id)}
                    className={`flex items-center text-sm font-medium px-4 py-2 rounded-lg transition-all ${
                      isExpanded
                        ? 'bg-[#ca9a37] text-white hover:bg-[#b88a2f]'
                        : 'bg-white text-gray-500 hover:text-[#ca9a37] border border-gray-200'
                    }`}
                  >
                    {isExpanded ? 'Hide' : 'Details'}
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 ml-1" />
                    ) : (
                      <ChevronDown className="w-4 h-4 ml-1" />
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default LoanProductsGrid;
